const cities = [
  { name: "Coral Gables", zips: "33134" },
  { name: "Miami", zips: "33101" },
  { name: "Coconut Grove", zips: "33133" },
  { name: "Pinecrest", zips: "33156" },
  { name: "South Miami", zips: "33143" },
  { name: "Kendall", zips: "33173" },
  { name: "Doral", zips: "33122" },
  { name: "Key Biscayne", zips: "33149" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or work in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function CoralGablesLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Coral Gables &amp; Miami-Dade County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Coral Gables, Coconut Grove, Pinecrest, or anywhere in Miami-Dade County, Fast Passport Center makes expedited passport processing simple — no waiting in lines, no driving across town, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Coral Gables is not merely a suburb of Miami — it is a self-contained world of Mediterranean Revival architecture, tree-canopied boulevards, and some of the most affluent neighborhoods in the southeastern United States. Conceived in the 1920s by George Merrick as "The City Beautiful," Coral Gables remains one of the most meticulously planned communities in America. The iconic Miracle Mile anchors a downtown of boutique shops, fine dining, and professional services that draw visitors from across Miami-Dade County. The Biltmore Hotel — a National Historic Landmark — rises above the city with its 300-foot tower, hosting corporate events, weddings, and international guests who arrive through Miami International Airport just six miles to the north.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The Venetian Pool — a spring-fed swimming complex carved from a coral rock quarry in 1924 — draws visitors from around the world and symbolizes the city's commitment to preserving its architectural and cultural heritage. But Coral Gables is not a museum piece. The University of Miami, located within the city limits, brings 17,000 students and a constant flow of international research, academic exchange, and study-abroad programs. The Lowe Art Museum and the Frost School of Music add cultural depth that attracts performers, scholars, and art collectors who travel internationally for exhibitions, concerts, and residencies.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Coral Gables' residential character is defined by its exclusive gated communities and historic estates. Gables Estates, Cocoplum, and Snapper Creek represent some of the most valuable real estate in Florida, home to C-suite executives, international investors, and multigenerational families with ties to Latin America, Europe, and Asia. Ponce de Leon Boulevard serves as the city's spine — a commercial corridor of banks, law firms, consulates, and international trade offices that positions Coral Gables as a legitimate business hub independent of downtown Miami or Brickell.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's international connectivity is unmatched by any comparable suburb in the United States. Miami International Airport — consistently ranked among the busiest international gateways in the world — sits just six miles north of Coral Gables city limits. MIA handles more international passengers than any other U.S. airport and serves as the primary hub for travel to Latin America and the Caribbean. For Coral Gables residents, a flight to Bogota, Sao Paulo, or Mexico City is often more convenient than a flight to Chicago. The Port of Miami — the busiest cruise port in the world — sits ten miles to the east, offering year-round Caribbean itineraries that draw families, retirees, and corporate groups from the Gables.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            This proximity to global transportation infrastructure means that Coral Gables residents do not think about international travel as a special occasion — it is a routine part of business, family, and educational life. A pharmaceutical executive living in Cocoplum may fly to Santiago quarterly. A University of Miami professor may travel to Barcelona for a conference with two weeks' notice. A family in Gables Estates may book a Caribbean cruise during Thanksgiving break. Each of these travelers faces the same risk: a passport that expired unnoticed, a child's five-year passport that timed out, or a damaged document discovered during packing.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Coral Gables travelers across every demographic and travel scenario. We understand that a business executive flying MIA to Mexico City has the same urgency as a UM student departing for a semester in Madrid. Our courier-based expedited service means you start online from your Miracle Mile office, your Gables Estates home, or your University of Miami dorm — and your passport arrives within days, not months. No visit to a regional agency required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Courier pickup available throughout Miami-Dade County
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Ship overnight or schedule a courier from Coral Gables, Coconut Grove, Pinecrest, South Miami, and surrounding areas</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location near Miracle Mile or MIA, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Miami-Dade County — start online, ship overnight, and we handle the rest. No need to drive to a regional agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Miami", href: "/miami" },
                  { name: "Fort Lauderdale", href: "/fort-lauderdale" },
                  { name: "Hollywood", href: "/hollywood" },
                  { name: "Aventura", href: "/aventura" },
                  { name: "Weston", href: "/weston" },
                  { name: "Sunny Isles", href: "/sunny-isles" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}